import React, { useState } from 'react';
import { ProductImage } from '../../types';
import './ImageGallery.css';

interface ImageGalleryProps {
  images: ProductImage[];
  productName: string;
}

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="500" height="500" viewBox="0 0 500 500"><rect width="500" height="500" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%2394a3b8">No Image Available</text></svg>';

const ImageGallery: React.FC<ImageGalleryProps> = ({ images = [], productName }) => {
  const [selectedImage, setSelectedImage] = useState(0);

  const validImages = Array.isArray(images) ? images.filter(img => img && img.image) : [];
  const safeIndex = selectedImage >= 0 && selectedImage < validImages.length ? selectedImage : 0;
  const currentImage = validImages[safeIndex];

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (validImages.length <= 1) return;
    setSelectedImage((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (validImages.length <= 1) return;
    setSelectedImage((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="image-gallery">
      {validImages.length > 1 && (
        <div className="thumbnail-list">
          {validImages.map((image, index) => (
            <div
              key={image.id ?? index}
              className={`thumbnail ${index === safeIndex ? 'active' : ''}`}
              onClick={() => setSelectedImage(index)}
            >
              <img
                src={image.image}
                alt={`${productName} thumbnail ${index + 1}`}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="main-image-container">
        <div className="main-image">
          <img
            src={currentImage?.image || PLACEHOLDER_IMAGE}
            alt={`${productName} - ${safeIndex + 1}`}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
          />
        </div>

        {validImages.length > 1 && (
          <>
            <button className="nav-btn prev" onClick={handlePrev} aria-label="Previous image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button className="nav-btn next" onClick={handleNext} aria-label="Next image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
